import axios from 'src/utils/axios';
import parseQueryParams from '../utils/query';

// TODO Remove these and refactor this service as well
export const REQUEST_STATUS = {
  WAITING: 'WAITING',
  PASSED: 'PASSED',
  FAILED: 'FAILED',
};

class UserService {
  // ULHHM (HHM_V2_READ always on): fetch the renter's certifications from the
  // certification list endpoint.  The queryset is scoped server-side by
  // Certification.objects.for_user (v2 CertificationMember chain), so no
  // legacy household_members filter is sent.  cert_household is expanded so
  // the renter questionnaire (TenantCertification) v2 path can read members.
  fetchRenterCertifications = async () => {
    const params = {
      expand: [
        'status',
        'property',
        'property.buildings',
        'applicant_pool',
        'unit',
        'unit.building',
        'category',
        'certification_config.properties',
        'cert_household',
        'form_signatures_request',
        'form_signatures_request.needed_signatures',
        'form_signatures_request.needed_signatures.household_member',
      ],
    };
    return await axios.get(
      `certification/certification/?${parseQueryParams(params)}`
    );
  };

  // New method specifically for renter contact information
  fetchUserProfileWithHouseholdMembers = async (userID) => {
    let response = {};
    const params = {
      // ENG-3557: household_members is a v2 method field on the backend —
      // the single token is what makes it emit; deeper tokens are inert.
      expand: 'household_members',
    };
    try {
      response = await axios.get(
        `user_profile/user/${userID}/?${parseQueryParams(params)}`
      );
      response.requestStatus = REQUEST_STATUS.PASSED;
    } catch (error) {
      response.requestStatus = REQUEST_STATUS.FAILED;
      response.error = error;
    }
    return response;
  };

  sendEmailVerification = async (payload) => {
    let response = {};
    try {
      // Use new endpoint that works with User.email directly
      response = await axios.post(
        `user_email_verification/send_verification/`,
        payload
      );
      return response;
    } catch {
      return response;
    }
  };

  sendPhoneVerificationCode = async (payload) => {
    let response = {};
    try {
      // Use new endpoint that works with User.phone_number directly
      response = await axios.post(
        `user_phone_verification/send_verification_code/`,
        payload
      );
      return response;
    } catch {
      return response;
    }
  };

  patchUserProfile = async (userID, payload) => {
    try {
      const response = await axios.patch(
        `user_profile/user/${userID}/`,
        payload
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  authenticateHubspotUser = async () => {
    return axios.post(`authenticate_hubspot_user/`);
  };

  logout = async (session_id) => {
    if (session_id) {
      return axios.patch(`release_impersonation/${session_id}/`);
    }
    return axios.post(`logout/`);
  };

  /**
   * Fetches certifications for the current user via the new v2 endpoint.
   * No user ID parameter — the backend uses request.user.
   */
  fetchMyCerts = async () => {
    return await axios.get('v2/renter-hub/me/my-certs/');
  };

  fetchMyHousehold = async () => {
    return await axios.get('v2/renter-hub/me/my-household/');
  };

  /**
   * Fetches form signature requests for the given certification IDs.
   * Called after certifications are loaded so the initial page renders fast.
   * @param {number[]} certificationIds - Certification IDs to fetch signatures for
   */
  fetchFormSignatureRequests = async () => {
    const { data } = await axios.get(
      'signature/signature_request_form/?compact=true'
    );
    return Array.isArray(data) ? data : [];
  };

  /**
   * Sends a signature reminder to household members who have pending
   * signatures on the given certification.
   * @param {number} certificationId
   */
  sendSignatureReminder = async (certificationId) => {
    const { data } = await axios.post(
      'v2/renter-hub/me/send-signature-reminder/',
      { certification_id: certificationId }
    );
    return data;
  };

  /**
   * Fetches unsigned documents for a specific household member on a cert.
   */
  fetchMemberUnsignedDocs = async (certificationId, memberUserId) => {
    const { data } = await axios.get(
      `v2/renter-hub/me/member-unsigned-docs/?certification_id=${certificationId}&member_user_id=${memberUserId}`
    );
    return data;
  };

  fetchSavedSignature = async () => {
    const { data } = await axios.get('signature/saved_signature/');
    return data;
  };

  fetchSavedInitials = async () => {
    const { data } = await axios.get('signature/saved_initials/');
    return data;
  };

  /**
   * Fetches per-cert status detail (IVR status text + action items) for the
   * new renter experience. Returns a Record<cert_id, {status_text, action_items}>.
   * @param {number[]} certificationIds - Certification IDs to fetch status for
   */
  fetchCertStatus = async (certificationIds) => {
    if (!certificationIds?.length) {
      return {};
    }
    const ids = certificationIds.join(',');
    const { data } = await axios.get(
      `v2/renter-hub/me/cert-status/?ids=${encodeURIComponent(ids)}`
    );
    return data ?? {};
  };
}

export const userService = new UserService();
